import { MediaType } from "constants/enums";
import find from "lodash/find";
import flatten from "lodash/flatten";

import {
  ProductDetailsFormCategoryAttributes,
  ProductFormValues,
  ProductVariantFormValue
} from "../components/forms/ProductDetailsForm/ProductDetailsForm.types";
import {
  ProductBothTogetherMethod,
  ProductVideoVariant
} from "../constants/enums";
import {
  ProductDetailsData,
  ProductDetailsVariant,
  ProductsListItem
} from "../product.types";

const getProductVideoVariant = (product: ProductDetailsData) => {
  const productVariantsImages = product.variants?.map(
    (variant) => variant.images
  );

  if (find(flatten(productVariantsImages), { mediaType: MediaType.VIDEO })) {
    return ProductVideoVariant.FOR_EACH_VIDEO;
  }

  if (product.generalVideo) {
    return ProductVideoVariant.ONE_MAIN_VIDEO;
  }

  return ProductVideoVariant.NO_VIDEO;
};

const getIdSet = (linkedProducts: ProductDetailsVariant[]) =>
  new Set(linkedProducts.map((product) => product.id).sort());

const checkAllVariantsHaveSameProducts = (
  arrays: ProductDetailsVariant[][]
) => {
  const referenceSet = getIdSet(arrays[0]);
  const referenceIds = [...referenceSet].sort().join(",");

  for (let i = 1; i < arrays.length; i++) {
    const currentSet = getIdSet(arrays[i]);
    const currentIds = [...currentSet].sort().join(",");
    if (currentIds !== referenceIds) {
      return false;
    }
  }

  return true;
};

const getProductBothTogetherMethod = (product: ProductDetailsData) => {
  if (
    product.variants.every(
      (variant) => variant.variantsBoughtTogether.length === 0
    )
  ) {
    return ProductBothTogetherMethod.NO_PRODUCTS;
  }

  if (
    checkAllVariantsHaveSameProducts(
      product.variants.map((variant) =>
        variant.variantsBoughtTogether.map(
          (variantBothTogether) => variantBothTogether.variant
        )
      )
    )
  ) {
    return ProductBothTogetherMethod.ONE_SET_FOR_ALL_VARIANTS;
  }

  return ProductBothTogetherMethod.PRODUCTS_FOR_EACH_VARIANT;
};

const getProductsListItemFromBoughtTogetherVariant = (
  variant: ProductDetailsVariant & {
    product: Omit<ProductDetailsData, "variants">;
  }
): ProductsListItem => {
  return {
    ...variant,
    image: variant.images.sort(
      (a, b) => Number(a.priority) - Number(b.priority)
    )[0],
    categoryName: variant.product.category.name,
    categoryId: variant.product.categoryId,
    price: variant.standardPrice,
    discountedPrice: variant.discount
      ? variant.standardPrice * (1 - variant.discount / 100)
      : variant.standardPrice - variant.discountAmount,
    visibility: variant.visibility
  };
};

export const getEditableProductFormValues = (
  product: ProductDetailsData,
  categoryAttributes: ProductDetailsFormCategoryAttributes
): ProductFormValues => {
  const sizes = product.variants.reduce(
    (
      acc: Array<{
        sizeAttributeValueId: string;
        label: string;
      }>,
      variant
    ) => {
      const newSizes = [...acc];

      variant.barcodes.forEach((barcode) => {
        if (
          newSizes.every(
            (size) =>
              size.sizeAttributeValueId !== barcode.sizeAttributeValue.id
          )
        ) {
          newSizes.push({
            sizeAttributeValueId: barcode.sizeAttributeValue.id,
            label: barcode.sizeAttributeValue.value
          });
        }
      });

      return newSizes;
    },
    []
  );

  const relatedAttributesValues =
    categoryAttributes?.productDetailsAttributes
      .filter((detailsAtt) => detailsAtt.id !== undefined)
      .map((detailsAtt) => {
        const productAttValue = product.attributeValues.find(
          (attValue) => attValue.attributeId === detailsAtt.id
        );
        return {
          attributeId: detailsAtt.id as string,
          value: productAttValue?.value ?? ""
        };
      }) ?? [];

  const additionalAttributesValuesIds =
    categoryAttributes?.additionalAttributes
      .filter((addAtt) => addAtt.id !== undefined)
      .map((addAtt) => {
        const productAttValue = product.attributeValues.find(
          (attValue) => attValue.attributeId === addAtt.id
        );
        return {
          attributeId: addAtt.id as string,
          valueId: productAttValue?.id ?? null
        };
      }) ?? [];

  const colors = product.variants.map((variant) => ({
    value: variant.colorAttributeValueId
  }));

  const variants: ProductVariantFormValue[] = product.variants.map(
    (productVariant) => ({
      name: productVariant.name,
      shortTitle: productVariant.shortTitle ?? "",
      sku: productVariant.sku,
      shortId: productVariant.shortId,
      badgeId: productVariant.badge?.id ?? null,
      colorAttributeValueId: productVariant.colorAttributeValueId,
      barcodes: sizes.map((size) => ({
        id: productVariant.barcodes.find(
          (barcode) =>
            barcode.sizeAttributeValue.id === size.sizeAttributeValueId
        )?.id,
        isDelete: false,
        sizeAttributeValueId: size.sizeAttributeValueId,
        value:
          productVariant.barcodes.find(
            (barcode) =>
              barcode.sizeAttributeValue.id === size.sizeAttributeValueId
          )?.value ?? ""
      })),
      variantsBoughtTogether: productVariant.variantsBoughtTogether.map(
        (bothTogetherItem) =>
          getProductsListItemFromBoughtTogetherVariant(bothTogetherItem.variant)
      ),
      images: productVariant.images,
      preview: productVariant.preview,
      compressedPreview: productVariant.compressedPreview,
      weight: productVariant.weight,
      volume: productVariant.volume,
      allowSellingBelowBuyingPrice: productVariant.allowSellingBelowBuyingPrice,
      buyingPrice: productVariant.buyingPrice,
      merchantPrice: productVariant.merchantPrice,
      standardPrice: productVariant.standardPrice,
      intermediateResellerPrice: productVariant.intermediateResellerPrice,
      confirmedResellerPrice: productVariant.confirmedResellerPrice,
      influencerPrice: productVariant.influencerPrice,
      recommendedSellingPrice: productVariant.recommendedSellingPrice
        ? productVariant.recommendedSellingPrice
        : null,
      discount: productVariant.discount || null,
      discountAmount: productVariant.discountAmount || null,
      activeDiscountType: productVariant.discount ? "percentage" : "amount",
      suggestionDiscount: productVariant.suggestionDiscount || null,
      suggestionDiscountAmount: productVariant.suggestionDiscountAmount || null,
      activeSuggestionDiscountType: productVariant.suggestionDiscountAmount
        ? "amount"
        : "percentage",
      commission: productVariant.commission || null,
      commissionPercentage: productVariant.commissionPercentage || null,

      activeCommissionType: productVariant.commissionPercentage
        ? "percentage"
        : "fixed",
      influencerCommissionPercentage:
        productVariant.influencerCommissionPercentage || null,
      bulkDiscounts: productVariant.quantityDiscounts
        .map(({ id, min, max, discount }) => ({
          id,
          isDelete: false,
          min,
          max,
          discount
        }))
        .sort((a, b) => a.min - b.min),
      id: productVariant.id,
      isDelete: false
    })
  );

  return {
    description: product.description,
    googleDriveLink: product.googleDriveLink ?? "",
    isChapshopChoice: product.isChapshopChoice,
    productVideoVariant: getProductVideoVariant(product),
    productBothTogetherMethod: getProductBothTogetherMethod(product),
    generalVideo: product?.generalVideo ? [product.generalVideo] : [],
    realVideo: product?.realVideo ? [product.realVideo] : [],
    sizes,
    relatedAttributesValues,
    additionalAttributesValuesIds,
    colors,
    variants
  };
};
